/**
 * @file Panneau de details de l'arme touchee (coffre ou emplacement) : nom, grade, type, degats / cadence / DPS / portee, comparaison
 * avec l'arme de l'emplacement vise, et actions directes (Equiper / Retirer, Fusionner, Verrouiller). Visible tant qu'une arme est selectionnee.
 */
import { GRADES } from '@shared/enums';
import { formatBig } from '@shared/format';
import type { Uid } from '@shared/types';
import { Component } from '../../core/Component';
import { h, setText, toggleClass } from '../../core/dom';
import { send } from '../../core/feedback';
import { uiIcon } from '../../core/uiIcons';
import type { UiContext } from '../../UiContext';
import { createTile, paintGrade } from './tile';

export interface WeaponDetailHooks {
  selected(): Uid | null;
  close(): void;
  /** Emplacement ou « Equiper » placerait l'arme (premier libre, sinon le dernier debloque). */
  targetSlot(): number;
  equip(uid: Uid, slot: number): void;
  merged(): void;
}

/** Intervalle de rafraichissement des valeurs (s) : les bonus (buffs, etoiles) changent en cours de partie. */
const REFRESH_S = 0.5;
const shots = (interval: number): string => {
  const v = 1 / interval;
  return v >= 10 ? Math.round(v).toString() : v.toFixed(1);
};

export class WeaponDetail extends Component {
  private key = '';
  private acc = 0;
  private tileHost!: HTMLElement;
  private name!: HTMLElement;
  private sub!: HTMLElement;
  private dmg!: HTMLElement;
  private rate!: HTMLElement;
  private dps!: HTMLElement;
  private range!: HTMLElement;
  private compare!: HTMLElement;
  private equipBtn!: HTMLButtonElement;
  private mergeBtn!: HTMLButtonElement;
  private lockBtn!: HTMLButtonElement;
  private hint!: HTMLElement;

  constructor(
    ctx: UiContext,
    private readonly hooks: WeaponDetailHooks,
  ) {
    super(ctx, 'div', 'weapon-detail is-hidden');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.el.setAttribute('data-ui-block', '');
    this.el.setAttribute('role', 'region');
    this.el.setAttribute('aria-live', 'polite');
    this.tileHost = h('div', { class: 'wd-tile' });
    this.name = h('b', { class: 'wd-name' });
    this.sub = h('span', { class: 'wd-sub' });
    const close = h('button', { class: 'btn round close wd-close', type: 'button', 'aria-label': t.t('common.close'), 'data-ui-block': true, 'data-fast': true, onclick: () => this.hooks.close() }, '\u2715');
    const stat = (icon: Node | string, label: string, value: HTMLElement): HTMLElement => h('div', { class: 'wd-stat', title: label }, h('span', { class: 'stat-ico' }, icon), h('span', { class: 'wd-stat-label' }, label), value);
    this.dmg = h('b', {});
    this.rate = h('b', {});
    this.dps = h('b', {});
    this.range = h('b', {});
    this.compare = h('p', { class: 'wd-compare' });
    this.equipBtn = h('button', { class: 'btn small', type: 'button', 'data-ui-block': true, onclick: () => this.onEquip() });
    this.mergeBtn = h('button', { class: 'btn small gem', type: 'button', 'data-ui-block': true, onclick: () => this.onMerge() });
    this.lockBtn = h('button', { class: 'btn small', type: 'button', 'data-ui-block': true, onclick: () => this.onLock() });
    this.hint = h('p', { class: 'hint wd-hint' }, t.t('weapons.detail.hint'));
    this.el.append(
      h('div', { class: 'wd-head' }, this.tileHost, h('div', { class: 'wd-title' }, this.name, this.sub), close),
      h(
        'div',
        { class: 'wd-stats' },
        stat(uiIcon('power'), t.t('stat.damage'), this.dmg),
        stat(uiIcon('speed'), t.t('stat.attackSpeed'), this.rate),
        stat(uiIcon('gun'), t.t('stat.dps'), this.dps),
        stat('\u2194', t.t('stat.range'), this.range),
      ),
      this.compare,
      h('div', { class: 'btn-row wd-actions' }, this.equipBtn, this.mergeBtn, this.lockBtn),
      this.hint,
    );
  }

  override update(dt: number): void {
    const uid = this.hooks.selected();
    const view = uid ? this.ctx.sim.query.weapons().find((w) => w.item.uid === uid) : undefined;
    toggleClass(this.el, 'is-hidden', !view);
    if (!view || !uid) {
      this.key = '';
      return;
    }
    const partner = this.ctx.sim.query.mergePartner(uid);
    const key = `${uid}|${view.item.defId}|${view.item.locked}|${view.equippedSlot}|${partner?.join(',') ?? ''}`;
    this.acc += dt;
    if (key === this.key && this.acc < REFRESH_S) return;
    this.acc = 0;
    const t = this.ctx.i18n;
    if (key !== this.key) {
      this.key = key;
      const tile = createTile(this.ctx, view.def, 'mini');
      tile.root.removeAttribute('role');
      tile.root.removeAttribute('tabindex');
      this.tileHost.replaceChildren(tile.root);
      paintGrade(this.ctx, this.el, view.def);
      setText(this.name, t.name(view.def.name));
      setText(this.sub, `${t.t('weapons.detail.grade', { grade: view.def.grade })} · ${t.t(`behavior.${view.def.behavior}`)}`);
      setText(this.equipBtn, view.equippedSlot === null ? t.t('common.equip') : t.t('weapons.unequip'));
      const next = GRADES[GRADES.indexOf(view.def.grade) + 1];
      toggleClass(this.mergeBtn, 'is-hidden', !partner);
      setText(this.mergeBtn, next ? t.t('weapons.detail.mergeTo', { grade: next }) : t.t('weapons.merge'));
      setText(this.lockBtn, view.item.locked ? t.t('weapons.unlock') : t.t('weapons.lock'));
      toggleClass(this.hint, 'is-hidden', view.equippedSlot !== null);
    }
    const slot = view.equippedSlot ?? this.hooks.targetSlot();
    const w = this.ctx.sim.query.weaponPreview(uid, slot);
    if (!w) return;
    setText(this.dmg, w.pellets > 1 ? `${formatBig(w.damage)} x${w.pellets}` : formatBig(w.damage));
    setText(this.rate, `${shots(w.interval)}/s`);
    setText(this.dps, formatBig(w.dps));
    setText(this.range, w.range.toFixed(1));
    // Comparaison avec l'arme deja dans l'emplacement vise (armes du coffre seulement).
    let cmp = '';
    let cls = '';
    if (view.equippedSlot === null) {
      const cur = this.ctx.sim.query.equippedWeapon(slot);
      if (!cur) cmp = t.t('weapons.detail.freeSlot', { n: slot + 1 });
      else if (cur.dps.gt(0)) {
        const pct = Math.round((w.dps.div(cur.dps).toNumber() - 1) * 100);
        cls = pct > 0 ? 'up' : pct < 0 ? 'down' : '';
        cmp = pct === 0 ? t.t('weapons.detail.sameAs', { n: slot + 1 }) : t.t('weapons.detail.vsSlot', { delta: `${pct > 0 ? '+' : ''}${pct} %`, n: slot + 1 });
      }
    } else cmp = t.t('weapons.detail.inSlot', { n: view.equippedSlot + 1 });
    setText(this.compare, cmp);
    this.compare.className = `wd-compare ${cls}`.trim();
  }

  private current(): { uid: Uid; slot: number | null; locked: boolean } | null {
    const uid = this.hooks.selected();
    const v = uid ? this.ctx.sim.query.weapons().find((w) => w.item.uid === uid) : undefined;
    return v ? { uid: v.item.uid, slot: v.equippedSlot, locked: v.item.locked } : null;
  }

  private onEquip(): void {
    const c = this.current();
    if (!c) return;
    if (c.slot === null) this.hooks.equip(c.uid, this.hooks.targetSlot());
    else {
      void send(this.ctx, { type: 'weapon.unequip', slot: c.slot });
      this.hooks.close();
    }
  }

  private onMerge(): void {
    const c = this.current();
    const pair = c ? this.ctx.sim.query.mergePartner(c.uid) : null;
    if (!pair) return;
    if (send(this.ctx, { type: 'weapon.merge', a: pair[0], b: pair[1] }).ok) this.hooks.merged();
  }

  private onLock(): void {
    const c = this.current();
    if (c) void send(this.ctx, { type: 'weapon.lock', uid: c.uid, locked: !c.locked });
    this.key = '';
  }
}
